import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plus, X, SlidersHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useDeliveries } from "@/lib/queries";
import { PHASES, PHASE_LABELS, weekLabel } from "@/lib/format";
import { followUp, followUpHref, isActionable, deliveryText, splitProjectName, weekText } from "@/lib/followup";
import { useScrollRestore } from "@/lib/hooks";
import { PageHeader, LoadingBlock, ErrorBlock, EmptyState, ActionLink } from "@/components/common/Blocks";
import { PhaseBadge } from "@/components/common/Badges";
import { NewCaseDialog } from "@/components/delivery/NewCaseDialog";
import { SplitPage } from "@/components/layout/SidePanel";
import { DeliveryPreview } from "@/components/delivery/DeliveryPreview";
import { CompanyMark, customerKind } from "@/components/common/CompanyMark";

const STORAGE_RESP = "leveranser:kolonne-ansvarlig";

const wk = (s) => s.dates?.agreed_delivery_week || s.dates?.desired_week || "";
const byName = (a, b) => a.project_name.localeCompare(b.project_name, "nb");
const SORTS = {
  levering: { label: "Levering", cmp: (a, b) => (wk(a) || "9999").localeCompare(wk(b) || "9999") || byName(a, b) },
  fase: { label: "Fase", cmp: (a, b) => PHASES.indexOf(a.phase) - PHASES.indexOf(b.phase) || (wk(a) || "9999").localeCompare(wk(b) || "9999") || byName(a, b) },
  kunde: { label: "Kunde", cmp: (a, b) => a.customer_name.localeCompare(b.customer_name, "nb") || byName(a, b) },
  leveranse: { label: "Leveranse", cmp: byName },
};
const VIEWS = [["aktive", "Aktive"], ["oppfolging", "Til oppfølging"], ["avsluttede", "Avsluttede"]];
const inView = (s, vis) => {
  if (vis === "alle") return true;
  if (vis === "avsluttede") return s.phase === "levert";
  if (vis === "oppfolging") return isActionable(followUp(s));
  return s.phase !== "levert";
};

/** Next step: a verb when something can be done here; quiet waiting text otherwise. */
const NextStepCell = ({ s, fu }) => {
  if (isActionable(fu)) {
    return (
      <div className="min-w-0" data-testid={`next-action-${s.number}`}>
        <ActionLink to={followUpHref(s)} testId={`row-action-${s.number}`} onClick={(e) => e.stopPropagation()}>{fu.verb}</ActionLink>
        {fu.title && fu.title !== fu.verb && <p className="t-meta mt-0.5 hidden truncate lg:block">{fu.title}</p>}
      </div>
    );
  }
  return (
    <p className={`min-w-0 ${fu.kind === "venter" ? "wait-text" : "t-table text-muted-foreground"}`} data-testid={`next-action-${s.number}`}>
      {fu.kind === "venter" && <Clock aria-label="Venter" />}<span className="truncate">{fu.title}</span>
    </p>
  );
};

const Chip = ({ children, onRemove, testId }) => (
  <span className="inline-flex h-7 items-center gap-1 rounded-md bg-secondary pl-2.5 pr-1 text-[13px]" data-testid={testId}>
    {children}
    <button type="button" onClick={onRemove} className="flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-background" aria-label="Fjern filter"><X className="h-3.5 w-3.5" /></button>
  </span>
);

const SortHead = ({ k, label, sort, dir, onSort, className = "" }) => {
  const active = sort === k;
  return (
    <th className={className} aria-sort={active ? (dir === "desc" ? "descending" : "ascending") : undefined}>
      <button type="button" onClick={() => onSort(k)} data-testid={`sort-${k}`} title={`Sorter på ${label.toLowerCase()}`}>
        {label}{active ? (dir === "desc" ? <ArrowDown /> : <ArrowUp />) : null}
      </button>
    </th>
  );
};

export default function LeveranserPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const phase = params.get("fase") || "alle";
  const resp = params.get("ansvarlig") || "alle";
  const week = params.get("uke") || "alle";
  const vis = params.get("vis") || "aktive";
  const sort = SORTS[params.get("sort")] ? params.get("sort") : "levering";
  const dir = params.get("retning") === "desc" ? "desc" : "asc";
  const selectedId = params.get("valgt");
  const [newOpen, setNewOpen] = useState(false);
  const [showResp, setShowResp] = useState(() => localStorage.getItem(STORAGE_RESP) === "1");
  const { data: all, isLoading, isError, refetch } = useDeliveries({});
  useScrollRestore("scroll:/leveranser", !!all);

  const setParam = useCallback((k, v, def = "alle") => setParams((p) => {
    const n = new URLSearchParams(p);
    if (!v || v === def) n.delete(k); else n.set(k, v);
    return n;
  }, { replace: true }), [setParams]);
  const closePanel = useCallback(() => setParam("valgt", null, null), [setParam]);

  const toggleResp = (v) => { setShowResp(v); localStorage.setItem(STORAGE_RESP, v ? "1" : "0"); };
  const onSort = (k) => {
    if (k === sort) setParam("retning", dir === "asc" ? "desc" : "asc", "asc");
    else setParams((p) => { const n = new URLSearchParams(p); n.set("sort", k); n.delete("retning"); if (k === "levering") n.delete("sort"); return n; }, { replace: true });
  };

  const responsibles = useMemo(() => Array.from(new Set((all || []).map((s) => s.responsible).filter(Boolean))).sort(), [all]);
  const weeks = useMemo(() => Array.from(new Set((all || []).map((s) => s.dates?.agreed_delivery_week).filter(Boolean))).sort(), [all]);

  /* Search + filters first; the view (Aktive / Til oppfølging / Avsluttede) is applied on top so the counts stay meaningful. */
  const base = useMemo(() => {
    if (!all) return [];
    const ql = q.trim().toLowerCase();
    return all
      .filter((s) => phase === "alle" || s.phase === phase)
      .filter((s) => resp === "alle" || s.responsible === resp)
      .filter((s) => week === "alle" || s.dates?.agreed_delivery_week === week)
      .filter((s) => !ql || [s.number, s.project_name, s.customer_name, s.stair_summary].some((v) => (v || "").toLowerCase().includes(ql)));
  }, [all, q, phase, resp, week]);
  const viewCounts = useMemo(() => Object.fromEntries(VIEWS.map(([k]) => [k, base.filter((s) => inView(s, k)).length])), [base]);
  const rows = useMemo(() => {
    const list = base.filter((s) => inView(s, vis)).map((s) => ({ s, fu: followUp(s) }));
    const cmp = SORTS[sort].cmp;
    list.sort((a, b) => (dir === "desc" ? -cmp(a.s, b.s) : cmp(a.s, b.s)));
    return list;
  }, [base, vis, sort, dir]);

  const selected = useMemo(() => (all || []).find((s) => s.id === selectedId) || null, [all, selectedId]);
  const activeFilters = [phase !== "alle", resp !== "alle", week !== "alle"].filter(Boolean).length;
  const hasAny = q || activeFilters > 0 || vis !== "aktive";
  const clear = () => setParams((p) => { const n = new URLSearchParams(); if (p.get("valgt")) n.set("valgt", p.get("valgt")); return n; }, { replace: true });
  const select = (s, e) => {
    if (e && e.target.closest("a, button")) return;
    setParam("valgt", s.id === selectedId ? null : s.id, null);
  };
  const onRowKey = (s) => (e) => { if (e.key === "Enter" || e.key === " ") { if (e.target.closest("a, button")) return; e.preventDefault(); select(s); } };

  return (
    <SplitPage panel={selected ? <DeliveryPreview summary={selected} onClose={closePanel} /> : null} onClose={closePanel} title="Leveranse">
      <div className="page" data-testid="deliveries-page">
        <PageHeader
          title="Leveranser"
          meta={all ? `${rows.length} ${vis === "oppfolging" ? "til oppfølging" : vis === "avsluttede" ? "avsluttede" : vis === "alle" ? "totalt" : "aktive"} · ${all.length} totalt` : null}
          actions={<Button size="lg" onClick={() => setNewOpen(true)} data-testid="new-case-button"><Plus /> Ny forespørsel</Button>}
        />

        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="deliveries-toolbar">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setParam("q", e.target.value, "")} placeholder="Søk på prosjekt, kunde eller nummer" className="h-9 border-0 bg-[hsl(var(--surface-sunken))] pl-9 shadow-none focus-visible:ring-1" data-testid="deliveries-search-input" aria-label="Søk i leveranser" />
          </div>
          <div className="seg" role="group" aria-label="Vis" data-testid="visibility-filter">
            {VIEWS.map(([k, l]) => (
              <button key={k} type="button" aria-pressed={vis === k} onClick={() => setParam("vis", k, "aktive")} data-testid={`visibility-${k}`}>{l} <span className="seg-count">{viewCounts[k]}</span></button>
            ))}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="ctl" data-active={activeFilters > 0} data-testid="filter-button" aria-label="Filtrer">
                <SlidersHorizontal /> Filter{activeFilters > 0 && <span className="tabular">({activeFilters})</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 space-y-4" data-testid="filter-popover">
              <div className="space-y-1.5">
                <Label className="t-label">Fase</Label>
                <Select value={phase} onValueChange={(v) => setParam("fase", v)}>
                  <SelectTrigger className="h-9" data-testid="phase-filter" aria-label="Fase"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle faser</SelectItem>
                    {PHASES.map((p) => <SelectItem key={p} value={p} data-testid={`phase-filter-${p}`}>{PHASE_LABELS[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="t-label">Ansvarlig</Label>
                <Select value={resp} onValueChange={(v) => setParam("ansvarlig", v)}>
                  <SelectTrigger className="h-9" data-testid="responsible-filter" aria-label="Ansvarlig"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle ansvarlige</SelectItem>
                    {responsibles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="t-label">Avtalt leveringsuke</Label>
                <Select value={week} onValueChange={(v) => setParam("uke", v)}>
                  <SelectTrigger className="h-9" data-testid="week-filter" aria-label="Leveringsuke"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle uker</SelectItem>
                    {weeks.map((w) => <SelectItem key={w} value={w}>{weekLabel(w)} · {w.split("-W")[0]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <Label htmlFor="col-resp" className="text-[14px]">Vis kolonnen Ansvarlig</Label>
                <Switch id="col-resp" checked={showResp} onCheckedChange={toggleResp} data-testid="toggle-responsible-column" />
              </div>
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="ctl" data-active={sort !== "levering" || dir !== "asc"} data-testid="sort-button" aria-label="Sorter">
                <ArrowUpDown /> {SORTS[sort].label} {dir === "desc" ? <ArrowDown className="!h-3.5 !w-3.5" /> : <ArrowUp className="!h-3.5 !w-3.5" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56" data-testid="sort-menu">
              <DropdownMenuLabel className="t-label">Sorter på</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sort} onValueChange={(k) => setParams((p) => { const n = new URLSearchParams(p); if (k === "levering") n.delete("sort"); else n.set("sort", k); return n; }, { replace: true })}>
                {Object.entries(SORTS).map(([k, v]) => <DropdownMenuRadioItem key={k} value={k} data-testid={`sort-option-${k}`}>{v.label}</DropdownMenuRadioItem>)}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={dir} onValueChange={(d) => setParam("retning", d, "asc")}>
                <DropdownMenuRadioItem value="asc" data-testid="sort-dir-asc">Stigende</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="desc" data-testid="sort-dir-desc">Synkende</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {(activeFilters > 0 || vis === "alle") && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
            {vis === "alle" && <Chip onRemove={() => setParam("vis", "aktive", "aktive")} testId="chip-vis">Alle leveranser</Chip>}
            {phase !== "alle" && <Chip onRemove={() => setParam("fase", "alle")} testId="chip-fase">Fase: {PHASE_LABELS[phase]}</Chip>}
            {resp !== "alle" && <Chip onRemove={() => setParam("ansvarlig", "alle")} testId="chip-ansvarlig">Ansvarlig: {resp}</Chip>}
            {week !== "alle" && <Chip onRemove={() => setParam("uke", "alle")} testId="chip-uke">Levering: {weekText(week)}</Chip>}
            {hasAny && <button type="button" className="link-quiet text-[13px]" onClick={clear} data-testid="clear-filters">Nullstill</button>}
          </div>
        )}

        {isLoading ? <LoadingBlock rows={8} /> : isError ? <ErrorBlock onRetry={refetch} /> : rows.length === 0 ? (
          <EmptyState title={vis === "oppfolging" && !q && activeFilters === 0 ? "Ingenting til oppfølging" : "Ingen leveranser matcher"} description={vis === "oppfolging" && !q && activeFilters === 0 ? "Alle aktive leveranser venter på andre eller går sin gang." : "Prøv et annet søk eller fjern et filter."} action={hasAny ? <Button variant="outline" size="sm" onClick={clear}>Nullstill filter</Button> : null} />
        ) : (
          <div className="frame overflow-x-auto">
            <table className="data-table" data-testid="deliveries-table">
              <thead>
                <tr>
                  <SortHead k="leveranse" label="Leveranse" sort={sort} dir={dir} onSort={onSort} className="w-[30%] min-w-[200px]" />
                  <SortHead k="kunde" label="Kunde" sort={sort} dir={dir} onSort={onSort} className="hidden min-w-[160px] md:table-cell" />
                  <SortHead k="fase" label="Fase" sort={sort} dir={dir} onSort={onSort} className="hidden w-[120px] sm:table-cell" />
                  <SortHead k="levering" label="Levering" sort={sort} dir={dir} onSort={onSort} className="hidden w-[130px] sm:table-cell" />
                  <th className="hidden w-[30%] lg:table-cell">Neste steg</th>
                  {showResp && <th className="hidden w-[140px] xl:table-cell">Ansvarlig</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ s, fu }) => {
                  const del = deliveryText(s.dates);
                  const { name, unit } = splitProjectName(s);
                  const second = [unit, s.number, !unit && s.stair_type && s.stair_type !== "Ikke avklart" ? s.stair_type : null, !unit && !s.stair_type && s.building_type ? s.building_type : null].filter(Boolean).join(" · ");
                  const isSel = s.id === selectedId;
                  return (
                    <tr key={s.id} className="is-link" aria-selected={isSel} tabIndex={0} onClick={(e) => select(s, e)} onKeyDown={onRowKey(s)} data-testid={`delivery-row-${s.number}`}>
                      <td>
                        <Link to={`/leveranser/${s.id}`} className="row-name transition-colors hover:text-primary hover:underline hover:underline-offset-[3px]" data-testid={`delivery-name-${s.number}`}>{name}</Link>
                        <p className="t-meta mt-0.5" data-testid={`delivery-unit-${s.number}`}>{second}</p>
                        <p className="t-meta mt-0.5 md:hidden">{[s.customer_name, <span key="p" className="sm:hidden">{PHASE_LABELS[s.phase]}</span>, del.sub ? del.full : null].filter(Boolean).reduce((acc, x, i) => (i === 0 ? [x] : [...acc, " · ", x]), [])}</p>
                        <div className="mt-1.5 lg:hidden"><NextStepCell s={s} fu={fu} /></div>
                      </td>
                      <td className="hidden md:table-cell">
                        <div className="flex items-center gap-2.5">
                          <CompanyMark name={s.customer_name} type={s.customer_type} logo={s.customer_logo} size="sm" slot />
                          <div className="min-w-0">
                            <p className="t-table truncate">{s.customer_name}</p>
                            {s.customer_type !== "privat" && <p className="t-meta mt-0.5 truncate">{customerKind(s)}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell"><PhaseBadge phase={s.phase} /></td>
                      <td className="hidden sm:table-cell">
                        <p className={`t-table tabular ${del.sub ? "font-medium" : "text-muted-foreground"}`} data-testid={`delivery-week-${s.number}`}>{del.main}</p>
                        {del.sub && <p className="t-meta mt-0.5">{del.sub === "avtalt" ? "Avtalt" : "Ønsket"}</p>}
                      </td>
                      <td className="hidden lg:table-cell"><NextStepCell s={s} fu={fu} /></td>
                      {showResp && <td className="hidden t-table text-muted-foreground xl:table-cell">{s.responsible}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <NewCaseDialog open={newOpen} onOpenChange={setNewOpen} />
      </div>
    </SplitPage>
  );
}
